import { errors, promise } from '@codegrade/utils';
import { ApiResultError } from './ApiError';
import * as utils from './utils';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function coerceToString(obj) {
    if (obj == null)
        return '';
    else if (typeof obj === 'string')
        return obj;
    return obj.toString();
}
let isBlob;
if (typeof Blob === 'undefined') {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isBlob = (_) => false;
}
else {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isBlob = (value) => value instanceof Blob;
}
let isFormData;
if (typeof FormData === 'undefined') {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isFormData = (_) => false;
}
else {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    isFormData = (value) => value instanceof FormData;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function getQueryString(params) {
    if (params == null) {
        return '';
    }
    const items = Object.entries(params);
    if (items.length === 0) {
        return '';
    }
    const query = items.reduce((acc, [key, value]) => {
        if (value != null) {
            acc.append(key, coerceToString(value));
        }
        return acc;
    }, new URLSearchParams());
    return `?${query.toString()}`;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function getUrl(config, requestConfig) {
    const path = requestConfig.path.replace(/[:]/g, '_');
    const url = `${config.base}${path}`;
    const query = getQueryString(requestConfig.query);
    if (query) {
        return `${url}${query}`;
    }
    return url;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
async function resolve(resolver, config, requestConfig) {
    if (typeof resolver !== 'function') {
        return resolver;
    }
    return await resolver(config, requestConfig);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
async function getHeaders(config, requestConfig) {
    const [token, configHeaders] = await Promise.all([
        resolve(config.token, config, requestConfig),
        resolve(config.headers, config, requestConfig),
    ]);
    const result = {};
    configHeaders.extract()?.forEach((value, key) => {
        result[key] = value;
    });
    result.Accept = 'application/json';
    requestConfig.headers?.forEach((value, key) => {
        result[key] = value;
    });
    token.ifJust(tokenValue => {
        result.Authorization = `Bearer ${tokenValue}`;
    });
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (requestConfig.body) {
        let value;
        if (isBlob(requestConfig.body)) {
            value = requestConfig.body.type || 'application/octet-stream';
        }
        else if (!isFormData(requestConfig.body)) {
            value = 'application/json';
        }
        if (value != null) {
            result['Content-Type'] = value;
        }
    }
    return result;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function isRetryableError(error) {
    if (error == null || !(error instanceof Error)) {
        return false;
    }
    const type = error.type;
    const code = error.code;
    const message = error.message;
    return (type !== 'aborted' &&
        code !== 'ERR_UNESCAPED_CHARACTERS' &&
        message !== 'Request path contains unescaped characters');
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
async function sendRequestWithHeaders(config, requestOptions, headers) {
    return await promise.retry(() => config.fetch(requestOptions.url, {
        method: requestOptions.method,
        headers,
        body: requestOptions.body,
    }), {
        shouldRetry: (nthTry, err) => nthTry < config.maxTries && isRetryableError(err),
        shouldRetryOk: (nthTry, result) => nthTry < config.maxTries && config.shouldRetry(requestOptions, result),
        fixupDelay: (_, nthTry) => config.getSleepTime(nthTry),
    });
}
const baseResponses = Object.freeze({
    400: (response) => Promise.resolve(ApiResultError.fromResponse(new Error('Bad Request'), response)),
    401: (response) => Promise.resolve(ApiResultError.fromResponse(new Error('Unauthorized'), response)),
    403: (response) => Promise.resolve(ApiResultError.fromResponse(new Error('Forbidden'), response)),
    404: (response) => Promise.resolve(ApiResultError.fromResponse(new Error('Not Found'), response)),
    500: (response) => Promise.resolve(ApiResultError.fromResponse(new Error('Internal Server Error'), response)),
    502: (response) => Promise.resolve(ApiResultError.fromResponse(new Error('Bad Gateway'), response)),
    503: (response) => Promise.resolve(ApiResultError.fromResponse(new Error('Service Unavailable'), response)),
});
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
async function makeResponse(options, result) {
    const responses = options.responses ?? {};
    const status = result.status;
    let response = responses[status];
    if (response == null) {
        response = responses[Math.trunc(-(status / 100))];
    }
    if (response == null) {
        response = baseResponses[status];
    }
    if (response == null) {
        /**
         * Default response parser for unknown HTTP status codes.
         *
         * This function is used when the status code of the response does not match any
         * predefined or custom response parsers. It creates an `ApiResultError` with a
         * generic error message indicating an unknown HTTP error.
         *
         * @param resp The response object from the fetch request.
         * @returns A promise that resolves to an `ApiResultError` containing the error message
         *          and the response object.
         */
        response = (resp) => Promise.resolve(ApiResultError.fromResponse(new Error(`Unknown HTTP error: ${status}`), resp));
    }
    return utils.filterApiResultError(await response(result));
}
/**
 * Request using fetch client.
 *
 * @param config The config to use.
 * @param partialRequest The request so far to use.
 * @returns The response from the backend.
 */
export async function request(config, partialRequest) {
    const requestConfig = { ...partialRequest, url: getUrl(config, partialRequest) };
    const headers = await getHeaders(config, requestConfig);
    let result;
    try {
        result = await sendRequestWithHeaders(config, requestConfig, headers);
        return await makeResponse(requestConfig, result);
    }
    catch (err) {
        throw config.processError.mapOrDefault(onError => onError({ ...requestConfig, headers: new Headers(headers) }, errors.coerceToError(err)), err);
    }
}
/**
 * Call `request` with parameters instead of an object.
 *
 * This minifies better than using an object, which is why this method exists.
 *
 * @template T The expected response type.
 * @param config See `Config`.
 * @param method See `ApiRequestOptions.method`.
 * @param path See `ApiRequestOptions.path`.
 * @param responses See `ApiRequestOptions.responses`.
 * @param body See `ApiRequestOptions.body`.
 * @param query See `ApiRequestOptions.query`.
 * @param headers See `ApiRequestOptions.headers`.
 * @param cookies See `ApiRequestOptions.cookies`.
 * @param formData See `ApiRequestOptions.formData`.
 * @returns A promise that resolves to the response from the backend, or an error if the request fails.
 */
export async function requestParams(config, method, path, responses, body, query, headers, cookies, formData) {
    return await request(config, {
        method,
        path,
        cookies,
        headers,
        query,
        formData,
        body,
        responses,
    });
}
